import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingOutlined } from '@ant-design/icons'
import { message } from 'antd'
import quoteDetailsForInviteAndMember from '@Quotes/components/QuoteShare/query'
import { salesOrderLines_salesOrderLines_items_quoteModelRevision_quoteModel_quote_user as BasicUser } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrderLines'

import getErrorMessage from '~/lib/getErrorMessage'
import logger from '~/lib/logger'
import { OWNER, COLLABORATOR, INVITE_TYPE, MEMBER_TYPE } from '../constants'

type TeamManagementActionsPropsType = {
    currentUser: number
    destroyInvite: Function
    id: string
    isLocatedInAdminPage: boolean
    isLocatedInBusinessAccountDetails: boolean
    isQuoteOwner: boolean
    modelId: string
    oboUserId: number
    quoteOwner: BasicUser
    removeMembership: Function
    role: typeof OWNER | typeof COLLABORATOR
    showDeleteInvite: boolean
    typename: string
    user: BasicUser
}

interface RemoveButtonParamType {
    onRevokeHandler: () => Promise<null | undefined>
}

const RemoveButton = (configuration: RemoveButtonParamType) => {
    return (
        <span
            onClick={configuration.onRevokeHandler}
            style={{
                color: '#F54A4A',
                marginTop: '10px',
                cursor: 'pointer',
            }}
        >
            Remove
        </span>
    )
}

export default function TeamManagementActions(
    props: TeamManagementActionsPropsType,
) {
    const {
        currentUser,
        destroyInvite,
        id,
        isLocatedInAdminPage,
        isLocatedInBusinessAccountDetails,
        isQuoteOwner,
        modelId,
        oboUserId,
        quoteOwner,
        removeMembership,
        role,
        showDeleteInvite,
        typename,
        user,
    } = props

    const [deleting, setDeleting] = useState(false)

    const navigate = useNavigate()

    const onRevokeMember = async () => {
        if (!removeMembership) {
            return null
        }

        const removingSelf = user.id === currentUser

        setDeleting(true)

        const collaboratorsRefetchQuery = [
            {
                query: quoteDetailsForInviteAndMember,
                variables: { id: modelId },
            },
        ]

        const mustReturnToQuotesList =
            removingSelf &&
            !isLocatedInAdminPage &&
            !isLocatedInBusinessAccountDetails

        const variables = {
            id,
            actorOverrideId: oboUserId ?? null,
        }

        try {
            await removeMembership({
                variables,
                refetchQueries: mustReturnToQuotesList
                    ? []
                    : collaboratorsRefetchQuery,
            })

            message.success('Removed collaborator from quote')

            if (mustReturnToQuotesList) {
                navigate(`/pages/quotes`)
            }
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: `TeamManagementActions::Failed to remove collaborator`,
                    variables,
                },
            })
            message.error(getErrorMessage(err))
        } finally {
            if (!mustReturnToQuotesList) {
                setDeleting(false)
            }
        }
    }

    const onRevokeInvite = async () => {
        if (!destroyInvite) {
            return null
        }

        setDeleting(true)

        const actorSenderId =
            (isLocatedInAdminPage || isLocatedInBusinessAccountDetails) &&
            oboUserId
                ? oboUserId
                : null

        const variables = {
            id,
            actorOverrideId: actorSenderId,
        }

        try {
            await destroyInvite({
                variables,
                refetchQueries: [
                    {
                        query: quoteDetailsForInviteAndMember,
                        variables: {
                            id: modelId,
                            senderId: actorSenderId,
                        },
                    },
                ],
            })

            message.success('Removed collaborator from quote')
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: `TeamManagementActions::Failed to destroy invite`,
                    variables,
                },
            })
            message.error(getErrorMessage(err))
        } finally {
            setDeleting(false)
        }
    }

    let showDeleteMember
    const isCurrentUserCollaborator = user?.id === currentUser
    const isAdminOboQuoteOwner =
        (isLocatedInAdminPage || isLocatedInBusinessAccountDetails) &&
        oboUserId === quoteOwner?.id
    const isAdminOboCollaborator =
        (isLocatedInAdminPage || isLocatedInBusinessAccountDetails) &&
        !isAdminOboQuoteOwner &&
        user?.id === oboUserId

    if (deleting) {
        return <LoadingOutlined spin />
    }

    if (
        isQuoteOwner ||
        isCurrentUserCollaborator ||
        isAdminOboQuoteOwner ||
        isAdminOboCollaborator
    ) {
        if (typename === MEMBER_TYPE || typename === INVITE_TYPE) {
            showDeleteMember = role !== OWNER
        } else {
            showDeleteMember = user.id !== currentUser
        }
    }

    if (typename === MEMBER_TYPE) {
        return showDeleteMember ? (
            <RemoveButton onRevokeHandler={onRevokeMember} />
        ) : null
    }

    return isAdminOboQuoteOwner || showDeleteInvite ? (
        <RemoveButton onRevokeHandler={onRevokeInvite} />
    ) : null
}
